import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  updateTenantMutation,
  getManagementTenantPreferencesOptions,
  type TenantRead,
} from "@harmonyso/tenants-sdk";
import { cellRequestOptions } from "@/lib/cells/cells";
import { invalidateCellTenantQueries } from "@/lib/cells/cell-queries";
import { Upload, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { useManagementTenantLogoUpload } from "@/hooks/use-tenant-logo-upload";
import { HarmonyLogoIcon } from "@/components/icons/harmony-logo-icon";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface LogoManagementDialogProps {
  tenant: TenantRead;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function LogoManagementDialog({
  tenant,
  open,
  onOpenChange,
}: LogoManagementDialogProps) {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const logoUploadMutation = useManagementTenantLogoUpload();

  const cellOptions = cellRequestOptions(tenant.deployment_cell);

  const { data: preferences } = useQuery({
    ...getManagementTenantPreferencesOptions({
      ...cellOptions,
      path: { tenant_id: tenant.id },
    }),
    enabled: open,
  });

  const preferencesLogoUrl = preferences?.logo_url;
  const showSyncButton =
    !selectedFile &&
    preferencesLogoUrl &&
    preferencesLogoUrl !== tenant.logo_url;

  const updateTenant = useMutation({
    mutationFn: updateTenantMutation().mutationFn,
    onSuccess: () => {
      invalidateCellTenantQueries(
        queryClient,
        tenant.deployment_cell,
        tenant.id,
      );
      toast.success("Logo updated successfully");
      onOpenChange(false);
      setSelectedFile(null);
      setPreviewUrl(null);
    },
    onError: (error: unknown) => {
      console.error("Failed to update tenant:", error);
      toast.error("Failed to update logo");
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/svg+xml", "image/jpeg"];
    const maxSize = 5 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      toast.error("Please select a PNG, SVG, or JPEG file.");
      return;
    }

    if (file.size > maxSize) {
      toast.error("File size must be less than 5MB.");
      return;
    }

    setSelectedFile(file);
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
  };

  const handleUpload = () => {
    if (!selectedFile) return;

    logoUploadMutation.mutate(
      {
        file: selectedFile,
        tenantId: tenant.id,
        deploymentCell: tenant.deployment_cell,
      },
      {
        onSuccess: (data) => {
          if ("logo_url" in data && typeof data.logo_url === "string") {
            updateTenant.mutate({
              ...cellOptions,
              path: { tenant_id: tenant.id },
              body: {
                logo_url: data.logo_url,
              },
            });
          }
        },
      },
    );
  };

  const handleSyncWithPreferences = () => {
    if (!preferencesLogoUrl) return;

    updateTenant.mutate({
      ...cellOptions,
      path: { tenant_id: tenant.id },
      body: {
        logo_url: preferencesLogoUrl,
      },
    });
  };

  const handleClose = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    onOpenChange(false);
  };

  const isLoading = logoUploadMutation.isPending || updateTenant.isPending;
  const displayUrl = previewUrl ?? tenant.logo_url;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Logo</DialogTitle>
          <DialogDescription>
            Upload a new logo for this tenant.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-4">
          <div
            className="flex h-48 w-48 items-center justify-center rounded-lg border-2"
            style={{ borderColor: "var(--border)" }}
          >
            {displayUrl ? (
              <img
                src={displayUrl}
                alt={`${tenant.company} logo`}
                className="h-full w-full rounded-lg object-contain p-4"
              />
            ) : (
              <HarmonyLogoIcon className="h-24 w-24 text-muted-foreground" />
            )}
          </div>

          <div className="flex w-full flex-col gap-2">
            <label htmlFor="logo-file-input">
              <Button
                variant="outline"
                className="w-full"
                disabled={isLoading}
                onClick={() => {
                  document.getElementById("logo-file-input")?.click();
                }}
                type="button"
              >
                <Upload className="mr-2 h-4 w-4" />
                {selectedFile ? "Change File" : "Select File"}
              </Button>
            </label>
            <input
              id="logo-file-input"
              type="file"
              accept="image/png,image/svg+xml,image/jpeg"
              onChange={handleFileSelect}
              className="hidden"
              disabled={isLoading}
            />

            {selectedFile && (
              <p className="text-center text-sm text-muted-foreground">
                {selectedFile.name}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between">
          <div className="flex gap-2">
            {showSyncButton && (
              <Button
                variant="secondary"
                onClick={handleSyncWithPreferences}
                disabled={isLoading}
                className="flex-1 sm:flex-initial"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Sync with Preference
                  </>
                )}
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 sm:flex-initial"
            >
              Cancel
            </Button>

            {selectedFile && (
              <Button
                onClick={handleUpload}
                disabled={isLoading}
                className="flex-1 sm:flex-initial"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}